import * as React from 'react'

import { Box, Button, Icon, Text, Tooltip, useToggle } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Loading from 'modules/core/components/Loading'
import Modal from 'modules/core/components/Modal'
import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { dateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import EmployeesSelector from 'modules/shift_management/components/ShiftManagement/EmployeesSelector'
import { useAddShiftsFromUnassigned } from 'modules/shift_management/hooks/useAddShiftsFromUnassigned'
import useDateRangeSelection from 'modules/shift_management/hooks/useDateRangeSelection'
import { useOnSelectEmployees } from 'modules/shift_management/hooks/useOnSelectEmployees'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useSelectableEmployees } from 'modules/shift_management/hooks/useSelectableEmployees'
import { buildAddShiftsInputParameters } from 'modules/shift_management/lib/buildAddShiftsInput'
import AssignEmployeesForm from 'modules/shift_management/lib/forms/AssignEmployeesForm'
import { UnassignedShift } from 'modules/shift_management/lib/types'

interface Props {
  unassignedShift: UnassignedShift
  totalAssignedShifts: number
  onClose: () => void
}

const AssignEmployeesModal = ({ unassignedShift, totalAssignedShifts, onClose }: Props) => {
  const [{ view }] = useSchedule()
  const {
    company: { id: companyId },
  } = useCurrentSession()

  const description = dateToString(unassignedShift.startAt, 'ui_long')
  const date = dateToString(unassignedShift.startAt, 'backend')

  const originalCounter = unassignedShift.counter
  const employeesNeeded = originalCounter - totalAssignedShifts
  const [open, toggle] = useToggle(false)

  const { loading: loadingEmployees, selectableEmployeeIds } = useSelectableEmployees(
    view.startAt,
    view.endAt
  )
  const form = useLazyRef(() => new AssignEmployeesForm()).current
  const [selectedEmployees, setSelectedEmployees] = React.useState<number[]>([])

  const daysByEmployee = useDateRangeSelection(selectedEmployees, date, date, '', true)

  const shifts = buildAddShiftsInputParameters({
    view,
    hasSelection: false,
    defaultAttributesByDate: {},
    name: null,
    unparsedStartTime: unassignedShift.startTime,
    unparsedEndTime: unassignedShift.endTime,
    daysByRow: daysByEmployee,
    defaultShiftId: unassignedShift.defaultShiftId,
    extraHours: unassignedShift.extraHours,
    notes: unassignedShift.notes,
    locationId: unassignedShift.locationId,
    workAreaId: unassignedShift.workAreaId,
    color: null,
    templateWeekId: null,
    unassignedShiftId: unassignedShift.id,
    state: 'draft',
    leaveId: undefined,
    companyId,
  })

  const { onSubmit, submitting } = useAddShiftsFromUnassigned({
    shifts,
    weekDays: '',
    isDefaultShift: !!unassignedShift.defaultShiftId,
    unassignedShiftCounter: unassignedShift.counter,
  })

  const { onSelectEmployee } = useOnSelectEmployees({
    form,
    selectedEmployees,
    setSelectedEmployees,
  })

  const disabled = !form.isDirty || selectedEmployees.length > employeesNeeded || submitting

  return (
    <Modal
      illustration="tasks_management"
      title={i18n.t('shift_management.shift_menu_options.assign_employees')}
      description={description}
      onSubmit={onSubmit}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose} type="secondary">
            {i18n.t('common.cancel')}
          </Button>
          <Button disabled={disabled} submit>
            {i18n.t('common.save')}
          </Button>
        </>
      }
    >
      {loadingEmployees ? (
        <Loading />
      ) : (
        <Box gap="s24">
          <Box gap="s4">
            <Box flexDirection="row" gap="s4" alignItems="center">
              <Text.Normal size="200" color="grey1000" weight="medium">
                {i18n.t('shift_management.coverage_section.unassigned_shift.amount_employees')}
              </Text.Normal>
              <Tooltip.Alt
                attachment="top center"
                open={open}
                toggle={toggle}
                label={i18n.t(
                  'shift_management.coverage_section.unassigned_shift.amount_employees_description'
                )}
              >
                <Icon.Small icon="info" encircled="grey800" />
              </Tooltip.Alt>
            </Box>
            <TextField value={employeesNeeded.toString()} readonly />
          </Box>

          <EmployeesSelector
            field={form.get('employee_id')}
            selectableEmployeeIds={selectableEmployeeIds}
            onSelectEmployee={onSelectEmployee}
            selectedEmployeeIds={selectedEmployees}
            width="s400"
          />
        </Box>
      )}
    </Modal>
  )
}

export default observer(AssignEmployeesModal)
